import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import API from '../utils/tenantApi';
import { useAuth } from '../context/AuthContext';

/**
 * TenantConsentPage — shown after invite flow (or on first portal visit without consent).
 * Tenant must explicitly accept or refuse electronic communication consent.
 */
export default function TenantConsentPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();

  const [portalData, setPortalData]       = useState(null);
  const [loading, setLoading]             = useState(true);
  const [checked, setChecked]             = useState(false);
  const [submitting, setSubmitting]       = useState(false);
  const [showRefuseConfirm, setShowRefuseConfirm] = useState(false);
  const [banner, setBanner]               = useState(null);

  // Load portal data to get address, email etc.
  useEffect(() => {
    API.get('/tenant/portal')
      .then(({ data }) => {
        // If already consented (or explicitly refused), skip this page
        if (data.tenant.consentGiven || data.tenant.consentDate) {
          navigate('/tenant/portal', { replace: true });
          return;
        }
        setPortalData(data);
      })
      .catch(() => navigate('/tenant/portal', { replace: true }))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const handleConsent = async () => {
    setSubmitting(true);
    try {
      await API.post('/tenant/consent', { consented: true, method: 'invite_flow' });
      navigate('/tenant/portal', { state: { consentBanner: 'given' }, replace: true });
    } catch {
      setSubmitting(false);
    }
  };

  const handleRefuse = async () => {
    setSubmitting(true);
    try {
      await API.post('/tenant/consent', { consented: false, method: 'invite_flow' });
      navigate('/tenant/portal', { state: { consentBanner: 'refused' }, replace: true });
    } catch {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-sm text-slate-500">Loading…</div>
      </div>
    );
  }

  const tenantEmail   = portalData?.tenant?.email   || user?.email   || '';
  const tenantName    = portalData?.tenant?.name    || user?.name    || 'You';
  const propertyAddr  = portalData?.property?.address || 'your rental property';

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-4 py-10">
      {/* Logo */}
      <Link to="/" className="inline-flex items-center gap-2 mb-8">
        <div className="w-8 h-8 bg-brand-600 rounded-lg flex items-center justify-center">
          <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
              d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
        </div>
        <span className="font-bold text-slate-800 text-lg">RentFriendly</span>
      </Link>

      <div className="w-full max-w-[560px] bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
        {/* Title */}
        <h1 className="text-xl font-bold text-slate-800 mb-2">One more thing before you get started</h1>
        <p className="text-sm text-slate-500 mb-6">
          Your landlord uses RentFriendly to manage your tenancy at{' '}
          <span className="font-medium text-slate-700">{propertyAddr}</span>.
        </p>

        {/* Section heading */}
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Electronic communication consent</p>

        {/* Explanation */}
        <p className="text-sm text-slate-600 leading-relaxed mb-4">
          Under Victorian tenancy law, your landlord can only send you official notices — like payment
          reminders and formal notices — by email if you agree to receive them this way.
        </p>
        <p className="text-sm text-slate-600 leading-relaxed mb-4">
          If you agree below, your landlord can send documents to{' '}
          <span className="font-medium text-slate-800">{tenantEmail}</span> electronically.
        </p>
        <p className="text-sm text-slate-600 leading-relaxed mb-5">
          You can withdraw this consent at any time by contacting your landlord or updating your
          preferences in this app.
        </p>

        {/* What you're consenting to */}
        <div className="space-y-2 mb-5">
          {[
            'Payment reminders via email',
            `Formal notices (including Notice to Vacate) via email to ${tenantEmail}`,
            'Other tenancy-related documents via email',
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-2">
              <svg className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-sm text-slate-700">{item}</span>
            </div>
          ))}
        </div>

        {/* Important note */}
        <div className="mb-6 flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <svg className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-blue-800">
            <strong>You are not required to consent.</strong> If you choose not to, your landlord must
            deliver formal notices by hand or post. This does not affect your tenancy in any other way.
          </p>
        </div>

        {/* Checkbox */}
        <label className="flex items-start gap-3 cursor-pointer mb-6">
          <input
            type="checkbox"
            checked={checked}
            onChange={e => setChecked(e.target.checked)}
            className="mt-0.5 w-5 h-5 rounded border-slate-300 text-brand-600 focus:ring-brand-500 cursor-pointer flex-shrink-0"
          />
          <span className="text-sm text-slate-700 leading-relaxed">
            I, <strong>{tenantName}</strong>, consent to receiving rental notices and documents from my
            landlord electronically at{' '}
            <span className="font-medium">{tenantEmail}</span>.
          </span>
        </label>

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setShowRefuseConfirm(true)}
            disabled={submitting}
            className="flex-1 px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl text-sm font-medium transition-colors min-h-[48px] disabled:opacity-50">
            I do not consent
          </button>
          <button
            type="button"
            onClick={handleConsent}
            disabled={!checked || submitting}
            className="flex-1 px-4 py-3 bg-brand-600 hover:bg-brand-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-xl text-sm font-semibold transition-colors min-h-[48px]">
            {submitting ? 'Saving…' : 'I agree — consent to electronic notices'}
          </button>
        </div>
      </div>

      {/* Refuse confirmation dialog */}
      {showRefuseConfirm && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setShowRefuseConfirm(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
              <h2 className="text-base font-semibold text-slate-800 mb-3">Are you sure?</h2>
              <p className="text-sm text-slate-600 mb-5">
                Your landlord will need to deliver all formal notices by hand or post. This is fine —
                it just means they cannot email them to you.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowRefuseConfirm(false)}
                  className="flex-1 px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-50 min-h-[44px]">
                  Go back
                </button>
                <button
                  onClick={() => { setShowRefuseConfirm(false); handleRefuse(); }}
                  disabled={submitting}
                  className="flex-1 px-4 py-2.5 bg-slate-700 hover:bg-slate-800 disabled:opacity-60 text-white rounded-xl text-sm font-medium min-h-[44px]">
                  Confirm — I do not consent
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
